import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["name", "id"];
import enviro from 'enviro';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { Redirect, Route, Switch, generatePath, useLocation, useParams } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import ApiError from '../components/Errors/ApiError';
import LearningPageMainSection from '../containers/TrackPage/LearningPageMainSection';
import UsageTracker from '../utils/UsageTracker';
import { VideoContentWithInteractiveEducation } from '../features/interactiveEducation/components/VideoContentWithInteractiveEducation';
import { buildInteractiveContentTrackingProps } from '../features/interactiveEducation/utils/contentUtils';
import { LEGACY_SHORT_CONTENT_URL_FRAGMENT, SHORT_CONTENT_URL_FRAGMENT } from '../constants/Navigation';
import { SCREENS } from '../constants/Tracking';
import { LearningContentPageLayout } from '../features/learningContent';
import { NextPlaylistContentOverview, PlaylistComplete, PlaylistIncompleteItems } from '../features/playlistContent';
import { RecommendationsForLearningContent, useSetLastClickedRecommendationItemFromUrl } from '../features/recommendations';
import { ShortContentDetails, ShortContentHeader, ShortContentInstructors, ShortContentPlaylistNavigation, ShortContentVideo, ShortsSurvey, useShort, useShortFromPropsOrParams } from '../features/shortContent';
import { useShortProgressById } from '../features/userProgress/userProgressQueries';
import { NavPageRoute } from '../features/navigation/components/NavPageRoute';
import { usePageTitle } from '../hooks/usePageTitle';
import { isInternalPortal } from '../features/accounts/components/ExternalPortalOnly';
import { useGetAiAccessControls } from '../features/aiControl/aiAccessQueries';
import { useVideoPlayerCaptions } from '../features/video/videoHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ShortContentBody = ({
  language,
  shortId
}) => {
  const {
    isTablet,
    isNotLaptop
  } = useMediaQuery();
  const {
    short
  } = useShort(shortId);
  const {
    playlistId
  } = useShortFromPropsOrParams();
  const videoId = short !== null && short !== void 0 && short.reference ? Number(short.reference) : undefined;
  const contentPortalId = short === null || short === void 0 ? void 0 : short.hubId;
  const professorId = short === null || short === void 0 ? void 0 : short.professors.map(professor => professor.id);
  const enableAdaptToIndustry = enviro.isQa() || isInternalPortal();
  const interactiveContentTrackingProps = buildInteractiveContentTrackingProps(short, 'SHORT_FORM_CONTENT', shortId, professorId);
  const {
    aiAccessControls,
    loading: aiAccessControlsLoading
  } = useGetAiAccessControls();
  const aiEnabled = !!(aiAccessControls !== null && aiAccessControls !== void 0 && aiAccessControls.generativeAiEnabled);
  const {
    captionsAvailable: videoCaptionsAvailable,
    captionsLoading: videoCaptionsLoading
  } = useVideoPlayerCaptions(videoId, short === null || short === void 0 ? void 0 : short.contentHublet, contentPortalId);
  const interactiveEducationEnabled = isNotLaptop && videoCaptionsAvailable && aiEnabled;
  useEffect(() => {
    if (short && !aiAccessControlsLoading && !videoCaptionsLoading) {
      const feature = interactiveEducationEnabled ? enableAdaptToIndustry ? ['AI-QUIZ-ME', 'AI-SUMMARIZATION', 'AI-ADAPT-TO-INDUSTRY'] : ['AI-QUIZ-ME', 'AI-SUMMARIZATION'] : [];
      const restContentTrackingProps = _objectWithoutPropertiesLoose(interactiveContentTrackingProps, _excluded);
      UsageTracker.trackStandalone('Pageview', Object.assign({
        screen: SCREENS.SHORT,
        contentId: shortId,
        contentName: short.name,
        feature
      }, restContentTrackingProps));
      if (window.scrollTo) {
        window.scrollTo(0, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveEducationEnabled]);
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "SHORT_CONTENT_LOADED",
    children: [/*#__PURE__*/_jsx(ShortsSurvey, {}), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "stretch",
      flexDirection: "column",
      gap: "900",
      children: [/*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(VideoContentWithInteractiveEducation, {
          contentPortalId: contentPortalId,
          contentType: "SHORT_FORM_CONTENT",
          creatorRegion: short === null || short === void 0 ? void 0 : short.contentHublet,
          interactiveContentTrackingProps: interactiveContentTrackingProps,
          language: language,
          primaryContentId: shortId,
          videoId: videoId,
          videoTitle: short === null || short === void 0 ? void 0 : short.name,
          children: ({
            displayInteractiveEducation,
            handleDisplayInteractiveEducation
          }) => /*#__PURE__*/_jsx(ShortContentVideo, {
            additionalTracking: interactiveContentTrackingProps,
            displayInteractiveEducation: displayInteractiveEducation,
            enableAdaptToIndustry: enableAdaptToIndustry,
            handleDisplayInteractiveEducation: handleDisplayInteractiveEducation,
            interactiveEducationEnabled: interactiveEducationEnabled
          })
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(Flex, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(ShortContentDetails, {})
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(View, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(ShortContentInstructors, {})
        })
      }), /*#__PURE__*/_jsx(LearningPageMainSection, {
        children: /*#__PURE__*/_jsx(View, {
          paddingInline: isTablet || playlistId ? '400' : undefined,
          children: /*#__PURE__*/_jsx(RecommendationsForLearningContent, {
            appearsIn: "short-content-recommendations",
            description: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.shortContent.videoRecommendations.description"
            }),
            direction: isTablet ? 'column' : 'row',
            id: shortId,
            language: language,
            size: isTablet ? 'small' : 'large',
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.shortContent.videoRecommendations.title"
            }),
            type: "SHORT_FORM_CONTENT"
          })
        })
      })]
    })]
  });
};
export const ShortPlaylistCompletionView = ({
  playlistNavigation,
  shortId
}) => {
  switch (playlistNavigation) {
    case 'next-in-playlist':
      return /*#__PURE__*/_jsx(NextPlaylistContentOverview, {
        navMarkerName: "SHORT_NEXT_IN_PLAYLIST_LOADED",
        shortId: shortId
      });
    case 'playlist-incomplete':
      return /*#__PURE__*/_jsx(PlaylistIncompleteItems, {
        navMarkerName: "SHORT_PLAYLIST_INCOMPLETE_LOADED"
      });
    default:
      return /*#__PURE__*/_jsx(PlaylistComplete, {
        navMarkerName: "SHORT_PLAYLIST_COMPLETE_LOADED"
      });
  }
};
export const ShortContent = () => {
  const {
    shortError,
    shortLoading,
    shortId,
    playlistId,
    playlistNavigation,
    short
  } = useShortFromPropsOrParams();
  useSetLastClickedRecommendationItemFromUrl({
    id: shortId ? Number(shortId) : undefined,
    type: 'SHORT_FORM_CONTENT'
  });
  const {
    shortStatus
  } = useShortProgressById(shortId);
  usePageTitle({
    i18nKey: 'learningCenter.dynamicPageTitles.shortVideoName',
    options: {
      videoName: short === null || short === void 0 ? void 0 : short.name
    },
    skip: !(short !== null && short !== void 0 && short.name)
  });
  if (!shortId) {
    return null;
  }
  if (shortLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 200
    });
  }
  if (shortError) {
    return /*#__PURE__*/_jsx(ApiError, {
      contentType: "SHORT_FORM_CONTENT",
      error: shortError,
      marker404: "SHORT_CONTENT_API_404",
      markerDefault: "SHORT_CONTENT_API_ERROR"
    });
  }
  return /*#__PURE__*/_jsx(LearningContentPageLayout, {
    header: /*#__PURE__*/_jsx(ShortContentHeader, {}),
    navigation: playlistId && short && /*#__PURE__*/_jsx(ShortContentPlaylistNavigation, {
      duration: short.duration,
      name: short.name,
      shortId: shortId,
      status: shortStatus || ''
    }),
    children: playlistNavigation ? /*#__PURE__*/_jsx(ShortPlaylistCompletionView, {
      playlistNavigation: playlistNavigation,
      shortId: shortId
    }) : short ? /*#__PURE__*/_jsx(ShortContentBody, {
      language: short === null || short === void 0 ? void 0 : short.language,
      playlistId: playlistId,
      shortId: shortId,
      useCases: short.useCases
    }) : /*#__PURE__*/_jsx(ApiError, {
      marker404: "SHORT_CONTENT_API_404"
    })
  });
};
const RedirectToLanguagelessShort = () => {
  const location = useLocation();
  const {
    shortId,
    playlistNavigation
  } = useParams();
  let newPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
    shortId
  });
  if (playlistNavigation) newPath += `/${playlistNavigation}`;
  return /*#__PURE__*/_jsx(Redirect, {
    to: Object.assign({}, location, {
      pathname: newPath
    })
  });
};
export const ShortContentRoute = () => {
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(NavPageRoute, {
        component: ShortContent,
        exact: true,
        pageTitleI18nKey: "learningCenter.pageTitles.shortVideo",
        path: SHORT_CONTENT_URL_FRAGMENT,
        shouldRenderCenteredContent: false,
        shouldRenderCompactSearchBar: true,
        shouldRenderTabs: false
      }), /*#__PURE__*/_jsx(Route, {
        component: RedirectToLanguagelessShort,
        exact: true,
        path: LEGACY_SHORT_CONTENT_URL_FRAGMENT
      })]
    })
  });
};
export default ShortContentRoute;